import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid2,
	Snackbar,
	SnackbarCloseReason,
	Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { LazyLoading } from "../../../../../controls/LazyLoading/LazyLoading";
import { Alert } from "../../../../../controls/alert";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import { Profile } from "../../models/Profile";
import { deleteProfile } from "../../repositories/ProfilesRepository";

type Props = {
	profile: Profile | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	suffixTitle?: string;
};

export const DeleteProfile = ({
	profile,
	show,
	onHide,
	suffixTitle,
}: Props) => {
	const [open, setOpen] = useState(false);
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [deleteAlert, setDeleteAlert] = useState(false);

	const handleClose = (
		event?: React.SyntheticEvent<any> | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
		setDeleteAlert(false);
	};

	const deleteProf = useMutation({
		mutationFn: deleteProfile,
		onSuccess: () => {
			setOpen(true);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setDeleteAlert(false);
			setIsSubmitLoading(false);
		},
	});

	const onDelete: (data: number) => Promise<void> = async (data: number) => {
		setIsSubmitLoading(true);
		await deleteProf.mutate(data);
	};

	useEffect(() => {
		if (show) {
			setIsLoading(false);
			setIsSubmitLoading(false);
		}
	}, [show]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Delete Profile${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<MesfModal
				title="DELETE PROFILE"
				open={show}
				handleClose={() => onHide(false)}
				id="delete-profile-modal"
				maxWidth="md"
			>
				<form>
					<MesfModal.Content style={{ padding: "15px 30px" }}>
						{isLoading && (
							<Grid2
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "150px" }}
							>
								<LazyLoading />
							</Grid2>
						)}
						{!isLoading && (
							<>
								<Grid2 container spacing={1}>
									<Grid2 size={{ md: 12, xs: 12 }}>
										<Typography>
											Are you sure you want to delete{" "}
											<strong>{profile?.ProfileName}</strong> profile?
										</Typography>
									</Grid2>
								</Grid2>
							</>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="secondary"
									onClick={() => setDeleteAlert(true)}
								>
									DELETE
								</Button>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
					<Dialog
						open={deleteAlert}
						onClose={() => handleClose()}
						aria-labelledby="responsive-dialog-title"
					>
						<DialogTitle id="responsive-dialog-title">{"WARNING"}</DialogTitle>
						<DialogContent>
							<DialogContentText style={{ padding: 0, margin: 0 }}>
								Be careful, once deleted you will not be able to recover this
								profile, are you sure you want to delete it?
							</DialogContentText>
						</DialogContent>
						<DialogActions style={{ padding: "16px 24px" }}>
							<Button
								onClick={() => setDeleteAlert(false)}
								autoFocus
								color="inherit"
							>
								CANCEL
							</Button>
							<Button
								startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
								autoFocus
								color="secondary"
								onClick={() => onDelete(profile?.ProfileId as number)}
							>
								Delete
							</Button>
						</DialogActions>
					</Dialog>
				</form>
			</MesfModal>
			<Snackbar
				open={open}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={handleClose}>
					The profile was deleted successfully
				</Alert>
			</Snackbar>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Deleting Profile"
			/>
		</>
	);
};
